import { useCallback, useEffect, useRef, type MutableRefObject } from 'react';
import {
  BANNER_ID,
  BANNER_CHANGE_EVENT,
} from 'talent_matching/components/table/background_ranking_banner';

export const GLOBAL_HEADER_ID = 'header';

type StickyHeaderOptions = {
  disabled?: boolean;
  refreshKey?: number;
};

type StickyHeaderApi = {
  wrapperRef: MutableRefObject<HTMLDivElement | null>;
};

const useStickyGridHeader = ({
  disabled = false,
  refreshKey = 0,
}: StickyHeaderOptions = {}): StickyHeaderApi => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const stickyOffsetRef = useRef<number>(0);

  const refreshOffset = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const anchor = document.getElementById(GLOBAL_HEADER_ID);
    const banner = document.getElementById(BANNER_ID);

    let computedOffset = anchor
      ? Math.round(anchor.getBoundingClientRect().bottom)
      : 0;

    if (banner) {
      computedOffset += Math.round(banner.getBoundingClientRect().height);
    }

    stickyOffsetRef.current = computedOffset;
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || disabled) {
      return;
    }

    refreshOffset();

    window.addEventListener('resize', refreshOffset);

    const anchor = document.getElementById(GLOBAL_HEADER_ID);
    let resizeObserver: ResizeObserver | undefined;

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => refreshOffset());
      if (anchor) {
        resizeObserver.observe(anchor);
      }
    }

    return () => {
      window.removeEventListener('resize', refreshOffset);
      resizeObserver?.disconnect();
    };
  }, [disabled, refreshOffset]);

  useEffect(() => {
    if (disabled) {
      return;
    }

    const container = wrapperRef.current;
    if (!container) {
      return;
    }

    let disposed = false;
    let cleanup: (() => void) | null = null;
    let rafId: number | null = null;

    const init = () => {
      if (disposed) {
        return;
      }

      const topContainer = container.querySelector<HTMLElement>(
        '.MuiDataGrid-topContainer'
      );
      const columnHeaders = container.querySelector<HTMLElement>(
        '.MuiDataGrid-columnHeaders'
      );
      const virtualScroller = container.querySelector<HTMLElement>(
        '.MuiDataGrid-virtualScroller'
      );

      if (!topContainer || !columnHeaders) {
        rafId = window.requestAnimationFrame(init);
        return;
      }

      const placeholder = document.createElement('div');
      placeholder.style.width = '100%';
      placeholder.style.display = 'none';
      placeholder.style.pointerEvents = 'none';
      topContainer.after(placeholder);

      let isFixed = false;

      const syncDimensions = () => {
        if (!isFixed) {
          return;
        }

        const wrapperRect = container.getBoundingClientRect();
        topContainer.style.left = `${wrapperRect.left}px`;
        topContainer.style.width = `${wrapperRect.width}px`;
      };

      const applyFixed = () => {
        const wrapperRect = container.getBoundingClientRect();
        const headerRect = topContainer.getBoundingClientRect();

        placeholder.style.height = `${headerRect.height}px`;
        placeholder.style.display = 'block';

        topContainer.style.position = 'fixed';
        topContainer.style.top = `${stickyOffsetRef.current}px`;
        topContainer.style.left = `${wrapperRect.left}px`;
        topContainer.style.width = `${wrapperRect.width}px`;
        topContainer.style.zIndex = '20';
        topContainer.style.boxShadow = '0 2px 6px rgba(25, 42, 62, 0.1)';

        isFixed = true;
      };

      const releaseFixed = () => {
        topContainer.style.position = '';
        topContainer.style.top = '';
        topContainer.style.left = '';
        topContainer.style.width = '';
        topContainer.style.zIndex = '';
        topContainer.style.boxShadow = '';

        placeholder.style.display = 'none';
        isFixed = false;
      };

      const syncHorizontalScroll = () => {
        if (!virtualScroller || !columnHeaders) {
          return;
        }

        columnHeaders.scrollLeft = virtualScroller.scrollLeft;
      };

      const updatePosition = () => {
        const wrapperRect = container.getBoundingClientRect();
        const headerHeight = topContainer.getBoundingClientRect().height;
        const { current: offset } = stickyOffsetRef;

        const shouldFix =
          wrapperRect.top <= offset &&
          wrapperRect.bottom > offset + headerHeight;

        if (shouldFix) {
          if (!isFixed) {
            applyFixed();
            syncHorizontalScroll();
          } else {
            topContainer.style.top = `${offset}px`;
            placeholder.style.height = `${headerHeight}px`;
            syncDimensions();
          }
        } else if (isFixed) {
          releaseFixed();
        }
      };

      const handleWindowScroll = () => {
        updatePosition();
        if (isFixed) {
          syncHorizontalScroll();
        }
      };

      const handleVirtualScroll = () => {
        if (isFixed) {
          syncHorizontalScroll();
        }
      };

      window.addEventListener('scroll', handleWindowScroll, { passive: true });
      window.addEventListener('resize', updatePosition);
      virtualScroller?.addEventListener('scroll', handleVirtualScroll, {
        passive: true,
      });

      const resizeObserver =
        typeof ResizeObserver !== 'undefined'
          ? new ResizeObserver(() => updatePosition())
          : undefined;

      resizeObserver?.observe(container);
      resizeObserver?.observe(topContainer);

      // Detect banner mount/unmount via custom event dispatched by
      // BackgroundRankingBanner. This avoids relying on DOM hierarchy
      // (the banner lives outside the DataViewTable subtree).
      const handleBannerChange = () => {
        refreshOffset();
        updatePosition();
      };
      window.addEventListener(BANNER_CHANGE_EVENT, handleBannerChange);

      updatePosition();

      cleanup = () => {
        window.removeEventListener('scroll', handleWindowScroll);
        window.removeEventListener('resize', updatePosition);
        virtualScroller?.removeEventListener('scroll', handleVirtualScroll);
        resizeObserver?.disconnect();
        window.removeEventListener(BANNER_CHANGE_EVENT, handleBannerChange);
        releaseFixed();
        placeholder.remove();
      };
    };

    init();

    return () => {
      disposed = true;
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      cleanup?.();
    };
  }, [disabled, refreshKey, refreshOffset]);

  return { wrapperRef };
};

export default useStickyGridHeader;
